// Entry credit for one account — balance, ledger, and a manual adjustment.
//
// A SEPARATE FILE, like GoingLivePanel, rather than more lines inside
// AdminApp.jsx. That file is over half a megabyte; every edit to it is an
// anchor-matching exercise, and this needs none of its surrounding state.
//
// TAKES CALLBACKS, NOT A TOKEN, matching how UserRow already receives
// onSave/onRemove/onResetPassword. The wsRequest calls stay in UsersPanel
// where the token lives, so this component never authenticates anything
// and can be rendered anywhere a user id is known.
import React, { useEffect, useState } from "react";
import { siteDateTime } from "./siteTime.js";


function formatWhen(ts) {
  if (!ts) return "";
  try {
    return siteDateTime(ts);
  } catch {
    return "";
  }
}

export default function UserCredits({ userId, onLoad, onAdjust, frozen, frozenTitle }) {
  const [state, setState] = useState(null);
  const [error, setError] = useState(null);
  const [amount, setAmount] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = (limit) => {
    setError(null);
    onLoad(userId, limit)
      .then((data) => {
        setState(data);
      })
      .catch((err) => {
        // THE 409 BRANCH IS GONE (1.261.0). This read used to be refused
        // outright while entry credit was switched off, and the panel hid
        // itself rather than showing a dead control. The read always
        // answers now — a balance exists on every site — so a failure here
        // is a real failure and says so.
        setError(err.message || "Could not reach backend.");
      });
  };

  useEffect(() => {
    load();
    // Deliberately keyed on the account, so expanding a different row
    // re-reads rather than showing the previous one's balance.
  }, [userId]);

  const submit = async (sign) => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      setError("Enter an amount.");
      return;
    }
    if (!reason.trim()) {
      setError("Give a reason — it goes in the ledger.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await onAdjust(userId, sign * value, reason.trim());
      setAmount("");
      setReason("");
      load();
    } catch (err) {
      setError(err.message || "Could not reach backend.");
    } finally {
      setBusy(false);
    }
  };

  const balances = (state?.balances || []).filter((b) => b.credits > 0);
  const ledger = state?.ledger || [];
  // ledgerTotal arrives from 1.406.0 onward; an older backend simply
  // reports the length it gave, so the "of N" and the button stay hidden
  // rather than the panel breaking.
  const total = Number(state?.ledgerTotal ?? ledger.length) || ledger.length;

  const inputStyle = {
    padding: 6,
    background: "var(--bg-input)",
    border: "1px solid var(--border-color-light)",
    borderRadius: 6,
    color: "var(--text-primary)",
  };

  return (
    <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border-color-light)" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <strong style={{ fontSize: 13 }}>Entry credit</strong>
        {state && balances.length === 0 && (
          <span style={{ fontSize: 13, opacity: 0.6 }}>No credit held</span>
        )}
        {balances.map((b) => (
          <span key={b.mode} style={{ fontSize: 13 }}>
            {b.credits} credit{b.credits === 1 ? "" : "s"}
            <span style={{ opacity: 0.55 }}> ({b.mode})</span>
          </span>
        ))}
        {!state && !error && <span style={{ fontSize: 13, opacity: 0.6 }}>Loading…</span>}
        <button
          type="button"
          onClick={() => setOpen(!open)}
          disabled={frozen}
          title={frozen ? frozenTitle : undefined}
          style={{ marginLeft: "auto" }}
        >
          {open ? "Close" : "Adjust"}
        </button>
      </div>

      {error && (
        <div style={{ fontSize: 12, color: "var(--danger, #c0392b)", marginTop: 6 }}>{error}</div>
      )}

      {open && (
        <div style={{ marginTop: 10 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              placeholder="Amount"
              style={{ ...inputStyle, width: 110 }}
            />
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (required)"
              style={{ ...inputStyle, flex: 1, minWidth: 180 }}
            />
            <button type="button" disabled={busy} onClick={() => submit(1)}>
              {busy ? "Working…" : "Add"}
            </button>
            <button type="button" disabled={busy} onClick={() => submit(-1)}>
              Remove
            </button>
          </div>
          <div style={{ fontSize: 11, opacity: 0.55, marginTop: 6 }}>
            Amounts are in WHOLE CREDITS, not money — that is the unit a balance is kept in.
              Removing more than the balance is refused rather than
            taking it negative. Every movement is recorded against your account.
          </div>
        </div>
      )}

      {ledger.length > 0 && (
        <details style={{ marginTop: 10 }}>
          <summary style={{ fontSize: 12, cursor: "pointer", opacity: 0.75 }}>
            Ledger ({ledger.length}
            {total > ledger.length ? ` of ${total}` : ""})
          </summary>
          <div style={{ marginTop: 6, maxHeight: 220, overflowY: "auto" }}>
            {ledger.map((e) => (
              <div
                key={e.id}
                style={{
                  display: "flex",
                  gap: 10,
                  fontSize: 12,
                  padding: "4px 0",
                  borderBottom: "1px solid var(--border-color-light)",
                }}
              >
                <span style={{ opacity: 0.55, minWidth: 130 }}>{formatWhen(e.at)}</span>
                <span style={{ minWidth: 70 }}>
                  {e.credits > 0 ? "+" : ""}
                    {e.credits}
                </span>
                <span style={{ opacity: 0.75, minWidth: 50 }}>{e.mode}</span>
                <span style={{ flex: 1 }}>{e.reason || ""}</span>
              </div>
            ))}
          </div>
          {/* WHAT IS NOT ON SCREEN, SAID PLAINLY. This read is capped, and
              the count above used to be the capped number with nothing to
              say so — which reads as the whole history to somebody
              investigating a balance, which is what this panel is for. */}
          {total > ledger.length && (
            <div style={{ fontSize: 11, marginTop: 6 }}>
              Showing the most recent {ledger.length} of {total} movements.{" "}
              <button type="button" onClick={() => load(1000)} disabled={busy}>
                Show all
              </button>
            </div>
          )}
          <div style={{ fontSize: 11, opacity: 0.5, marginTop: 4 }}>
            Amounts are in credits, the unit a balance is kept in.
          </div>
        </details>
      )}
    </div>
  );
}
